import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { CommonService } from 'src/app/service/common.service';
import { Country } from 'src/app/models/country.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, startWith, map } from 'rxjs/operators';

@Component({
  selector: 'app-country-picker',
  templateUrl: './country-picker.component.html',
  styleUrls: ['./country-picker.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryPickerComponent),
      multi: true,
    },
  ],
})
export class CountryPickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder: string = 'Select Country';
  @Input() displayFormat: 'code' | 'name' | 'both' = 'both'; // 'code' shows +963, 'name' shows Syria, 'both' shows Syria (+963)
  @Input() showFlag: boolean = true;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Output() countrySelected = new EventEmitter<Country>();

  countries$ = new BehaviorSubject<Country[]>([]);
  filteredCountries$ = new BehaviorSubject<Country[]>([]);
  selectedCountry$ = new BehaviorSubject<Country | null>(null);
  searchText$ = new BehaviorSubject<string>('');

  // Expose values for template (without async pipe)
  countries: Country[] = [];
  filteredCountries: Country[] = [];
  selectedCountry: Country | null = null;
  isOpen: boolean = false;
  
  private destroy$ = new Subject<void>();
  private onChangeCallback: any = null;
  private onTouchCallback: any = null;

  constructor(private commonService: CommonService) {
    // Subscribe to observables and update properties
    this.countries$.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.countries = value;
    });
    
    this.filteredCountries$.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.filteredCountries = value;
    });
    
    this.selectedCountry$.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.selectedCountry = value;
    });
  }

  ngOnInit(): void {
    this.loadCountries();
    this.setupSearch();
  }

  /**
   * Load countries from common service
   */
  private loadCountries(): void {
    this.commonService.getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const countries = response?.data || [];
          this.countries$.next(countries);
          this.filteredCountries$.next(countries);

          // Set default country if marked
          const defaultCountry = countries.find((c: Country) => c.is_default);
          if (defaultCountry) {
            this.selectCountry(defaultCountry);
          }
        },
        error: (error) => {
          console.error('Error loading countries:', error);
        },
      });
  }

  /**
   * Setup search/filter functionality
   */
  private setupSearch(): void {
    this.searchText$
      .pipe(
        startWith(''),
        map((searchValue: string) => this._filterCountries(searchValue)),
        takeUntil(this.destroy$)
      )
      .subscribe((filtered) => {
        this.filteredCountries$.next(filtered);
      });
  }

  /**
   * Filter countries based on search text
   */
  private _filterCountries(searchValue: string): Country[] {
    const filterValue = searchValue.toLowerCase().trim();
    if (!filterValue) {
      return this.countries$.value;
    }

    return this.countries$.value.filter(
      (country: Country) =>
        country.name.toLowerCase().includes(filterValue) ||
        country.country_code.toLowerCase().includes(filterValue) ||
        country.code.toLowerCase().includes(filterValue)
    );
  }

  /**
   * Handle search input change
   */
  onSearchChange(event: any): void {
    const searchValue = event.target?.value || '';
    this.searchText$.next(searchValue);
  }

  /**
   * Toggle dropdown open/closed
   */
  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.filteredCountries$.next(this.countries);
      setTimeout(() => {
        const searchInput = document.querySelector('.country-search-input') as HTMLInputElement;
        searchInput?.focus();
      }, 0);
    } else {
      this.searchText$.next('');
    }
  }

  /**
   * Close dropdown
   */
  closeDropdown(): void {
    this.isOpen = false;
    this.searchText$.next('');
  }

  /**
   * Select a country
   */
  selectCountry(country: Country): void {
    this.selectedCountry$.next(country);
    this.countrySelected.emit(country);
    this.onChangeCallback?.(country.country_code);
    this.searchText$.next('');
    this.closeDropdown();
  }

  /**
   * Get display text for country
   */
  getCountryDisplay(country: Country | null): string {
    if (!country) return '';

    switch (this.displayFormat) {
      case 'code':
        return country.country_code;
      case 'name':
        return country.name;
      case 'both':
      default:
        return `${country.name} (${country.country_code})`;
    }
  }

  /**
   * Track by function for performance
   */
  trackByCode(index: number, country: Country): string {
    return country.code;
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value) {
      const country = this.countries$.value.find((c: Country) => c.country_code === value);
      if (country) {
        this.selectedCountry$.next(country);
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
